// DatahubIntegrationPage.jsx
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  DialogActions,
  Divider,
  Grid,
  List,
  ListItemText,
  Popper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { getSelectedAccount } from '../../../redux/selectedAccountSlice';
import {
  useGetDatahubAccountByIdQuery,
  usePostDatahubIntegrationAccountMutation,
  usePostDatahubIntegrationAccountRetryMutation,
} from '../../../api/datahub-integration/datahubIntegrationApiSlice';
import { useNavigate } from 'react-router-dom';
import { cvrApiSlice } from '../../../api/datahub-integration/cvrApiSlice';
import DatahubIntegrationAccountCard from './DatahubIntegrationAccountCard';
import SearchIcon from '@mui/icons-material/Search';
import { showSnackbar } from '../../../redux/snackbarSlice';
import { useTranslation } from 'react-i18next';
/* ---------- display helpers ---------- */
const boolLabel = (v) => (v ? 'Yes' : 'No');

const statusChip = (status) => (
  <Chip
    size="small"
    label={status || 'Unknown'}
    color={status === 'Active' ? 'success' : 'default'}
    sx={{ fontWeight: 500 }}
  />
);

/* ---------- main component ---------- */
function DatahubIntegrationPage() {
  const { t } = useTranslation('datahub-integration');

  const selectedAccount = useSelector(getSelectedAccount);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    data: account,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetDatahubAccountByIdQuery(selectedAccount?.AccountId, {
    skip: !selectedAccount?.AccountId,
  });

  const [postDatahubIntegrationAccount, { isSuccess: isDIASuccess }] =
    usePostDatahubIntegrationAccountMutation();

  const [
    trigger,
    {
      data: data,
      isSuccess: isCompanyNumberSuccess,
      isError: isCompanyNumberError,
      isLoading: isCompanyNumberLoading,
    },
  ] = cvrApiSlice.endpoints.getCompany.useLazyQuery();

  const [companyNumber, setCompanyNumber] = useState('');
  const [helperText, setHelperText] = useState();
  const [enableSearch, setEnableSearch] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isPopperOpen, setIsPopperOpen] = useState(false);

  const handlePopperClose = () => {
    setIsPopperOpen(false);
    setEnableSearch(true);
  };

  const handleCompanyNumberInput = (e) => {
    setCompanyNumber(e.target.value);
    if (e.target.value !== '') {
      setEnableSearch(true);
    } else {
      setEnableSearch(false);
    }
  };
  const handleCompanyNumberSubmit = async (e) => {
    setAnchorEl(e.currentTarget);
    const result = await trigger({ companyNumber: companyNumber });

    if (result.isSuccess) {
      setEnableSearch(false);
      setIsPopperOpen(true);
    } else if (result.isError) {
      setHelperText(t('add-cvr.error-text'));
    }
  };

  const handleSubmit = () => {
    postDatahubIntegrationAccount({
      ReCalcAccountId: selectedAccount.AccountId,
      CustomerCVR: companyNumber,
      limitedMetersList: [],
      IncludeChildMeteringPoints: true,
    })
      .unwrap()
      .then((r) => {
        setIsPopperOpen(false);
      })
      .catch((e) => {
        if (e.status === 503) {
          dispatch(
            showSnackbar({
              message: t('snackbar.datahub-unavailable'),
              severity: 'error',
            }),
          );
        }
        if (e.status === 404) {
          dispatch(
            showSnackbar({ message: t('snackbar.no-auth'), severity: 'error' }),
          );
        }
        if (e.status === 403) {
          dispatch(
            showSnackbar({
              message: t('snackbar.not-allowed'),
              severity: 'error',
            }),
          );
        }
      });
  };
  const handleExternalGrantLink = () => {
    // dates are YYYY-MM-DD
    const fromDate = dayjs().subtract(3, 'year').format('YYYY-MM-DD');
    const toDate = dayjs().add(3, 'year').format('YYYY-MM-DD');
    const path = `https://eloverblik.dk/power-of-attorney?thirdPartyId=23C949D6-394A-4001-94EC-B4F55E9CF850&fromDate=${fromDate}&toDate=${toDate}&customerKey=${selectedAccount.AccountId}`;
    window.open(path, '_blank');
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Card variant="outlined">
        <CardHeader title={t('title')} />
        <CardContent>
          <Stack
            spacing={2}
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
            }}
          >
            <ListItemText
              sx={{ mb: 2 }}
              primary={t('give-access.title')}
              secondary={t('give-access.description')}
            />
            <Button
              variant="contained"
              onClick={() => handleExternalGrantLink()}
            >
              {t('give-access.button')}
            </Button>
            <Divider sx={{ width: '100%' }} />

            <ListItemText
              sx={{ mb: 2 }}
              primary={t('add-cvr.title')}
              secondary={t('add-cvr.description')}
            />

            <TextField
              margin="normal"
              id="companyNumber"
              name="companyNumber"
              autoComplete=""
              value={companyNumber}
              onChange={handleCompanyNumberInput}
              InputProps={{
                endAdornment: (
                  <Button
                    variant="contained"
                    onClick={(e) => handleCompanyNumberSubmit(e)}
                    disabled={!enableSearch}
                  >
                    <SearchIcon />
                  </Button>
                ),
              }}
              label={t('add-cvr.textfield-label')}
              InputLabelProps={{
                shrink: true,
              }}
              error={isCompanyNumberError}
              helperText={helperText}
            />
            <Popper open={isPopperOpen} placement="bottom" anchorEl={anchorEl}>
              {isCompanyNumberSuccess && (
                <CompanyData
                  data={data.stamdata}
                  onSubmit={() => handleSubmit()}
                  onClose={() => handlePopperClose()}
                />
              )}
              {isCompanyNumberLoading && <CircularProgress />}
            </Popper>
            {isLoading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {!isError && account?.length > 0 ? (
                  <>
                    <Divider sx={{ width: '100%' }} />
                    <List sx={{ width: '100%' }}>
                      {account.map((a) => {
                        return <DatahubIntegrationAccountCard account={a} />;
                      })}
                    </List>
                  </>
                ) : null}
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
}

export default DatahubIntegrationPage;

function CompanyData({ data, onClose, onSubmit }) {
  const { t } = useTranslation('datahub-integration');
  return (
    <Card sx={{ minWidth: 275 }}>
      <CardContent>
        <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
          {data.cvrnummer}
        </Typography>
        <Typography variant="h5" component="div">
          {data.navn}
        </Typography>
        <Typography sx={{ mb: 1.5 }} color="text.secondary">
          {data.adresse} {data.postnummerOgBy}
        </Typography>
      </CardContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          {t('add-cvr.popper.cancel')}
        </Button>

        <Button variant="contained" onClick={() => onSubmit()}>
          {t('add-cvr.popper.add')}
        </Button>
      </DialogActions>
    </Card>
  );
}
